import React, { useEffect, useMemo, useState } from 'react';
import {
  X, CalendarDays, Loader2,
  CheckCircle2, Circle, Clock, Layers3, MessageSquare, ShieldCheck, Ban,
} from 'lucide-react';
import studyBuilderService from '../../services/studyBuilderService';
import { getStudyDraftReviewContext } from '../../services/reviewWorkflowApi';
import ScheduleReviewCard, { SectionLabel, Chip, Muted } from './ScheduleReviewCard';
import SchedulePreviewModal from './SchedulePreviewModal';

/**
 * Full design detail for one study draft, opened from the Study Drafts list.
 *
 * Replaces the old in-row dropdown, which predated FECOA-1236 and only showed
 * the legacy assessment-window shape (raw day offsets + UTC start/end times) —
 * intra-day timepoints and their per-timepoint surveys were invisible. The
 * modal loads its own survey/reminder/review context and renders every
 * schedule through the shared ScheduleReviewCard, so it always matches the
 * full-page Study Design Review.
 */

const STATUS_META = {
  draft: { label: 'Draft', cls: 'badge-gray', Icon: Clock },
  in_review: { label: 'Pending Review', cls: 'badge-info', Icon: Clock },
  approved: { label: 'Approved', cls: 'bg-teal-50 text-teal-700 ring-teal-600/20 dark:bg-teal-500/10 dark:text-teal-300 dark:ring-teal-400/25', Icon: CheckCircle2 },
  denied: { label: 'Needs Revision', cls: 'badge-danger', Icon: MessageSquare },
  published: { label: 'Published', cls: 'badge-success', Icon: CheckCircle2 },
  superseded: { label: 'Superseded', cls: 'badge-gray', Icon: Layers3 },
};

const fmtDateTime = (v) => {
  if (!v) return null;
  try { return new Date(v).toLocaleString(); } catch { return v; }
};

// One figure in the stats strip at the top of the modal.
const StatTile = ({ label, value }) => (
  <div className="min-w-0 bg-white px-4 py-3 dark:bg-gray-800">
    <div className="text-[12px] text-gray-500 dark:text-gray-400">{label}</div>
    <div className="mt-1 truncate text-lg font-semibold leading-tight tabular-nums text-gray-900 dark:text-gray-50">{value}</div>
  </div>
);

const StudyDraftDetailModal = ({ open, draft, chain = [], onClose }) => {
  const [surveys, setSurveys] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [reviewCtx, setReviewCtx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    let active = true;
    if (!open || !draft?.id) return undefined;
    setLoading(true);
    Promise.all([
      studyBuilderService.getSurveysByStudyName(draft.name).catch(() => null),
      studyBuilderService.getReminderTemplates(draft.id).catch(() => null),
      getStudyDraftReviewContext(draft.id).catch(() => null),
    ])
      .then(async ([surveyData, templateData, ctx]) => {
        if (!active) return;
        const loaded = surveyData?.surveys || [];
        setReminders(templateData?.results || templateData || []);
        setReviewCtx(ctx);
        // The study-name lookup misses surveys when the draft name has drifted
        // from the Study title — resolve any still-unnamed ids individually,
        // inside the loading window so a bare "Survey #id" never flashes.
        const draftSchedules = Array.isArray(draft.data?.schedules) ? draft.data.schedules : [];
        const extras = await studyBuilderService
          .resolveMissingScheduleSurveys(draftSchedules, loaded)
          .catch(() => []);
        if (!active) return;
        setSurveys(extras.length > 0 ? [...loaded, ...extras] : loaded);
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, draft?.id, draft?.name, draft?.review_status]);

  // Escape closes the detail modal, unless the calendar visualizer is stacked
  // on top — then Escape belongs to it.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !previewOpen) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, previewOpen]);

  const surveyName = useMemo(() => (id) => {
    const found = surveys.find((s) => s.id === parseInt(id, 10));
    return found ? found.name : `Survey #${id}`;
  }, [surveys]);
  const reminderName = useMemo(() => (id) => {
    const found = reminders.find((r) => r.id === parseInt(id, 10));
    return found ? found.name : `Reminder #${id}`;
  }, [reminders]);

  if (!open || !draft) return null;

  const data = draft.data || {};
  const arms = Array.isArray(data.arms) ? data.arms : [];
  const schedules = Array.isArray(data.schedules) ? data.schedules : [];
  const startDay = schedules[0]?.studyStartDay;
  const status = STATUS_META[draft.review_status] || STATUS_META.draft;
  const StatusIcon = status.Icon;
  const rs = draft.review_status || 'draft';

  const readiness = [
    { label: 'Study start day set', done: startDay !== undefined },
    { label: 'Arms configured', done: arms.length > 0 },
    { label: 'Schedules configured', done: schedules.length > 0 },
    { label: 'Surveys assigned', done: schedules.some((s) => Array.isArray(s?.standardSurveys) && s.standardSurveys.length > 0) },
  ];
  const doneCount = readiness.filter((r) => r.done).length;

  return (
    <>
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/40 p-4 dark:bg-black/70"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-overlay ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Design details for ${draft.name}`}
      >
        {/* Header */}
        <div className="flex flex-shrink-0 items-start justify-between gap-4 border-b border-gray-100 px-6 pb-4 pt-5 dark:border-white/[0.06]">
          <div className="min-w-0">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <h2 className="truncate text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-50">{draft.name}</h2>
              {chain.length > 1 && (
                <span className="code-chip flex-shrink-0">
                  v{draft.version_number ?? 1}
                </span>
              )}
              <span className={`badge flex-shrink-0 ${status.cls}`}>
                <StatusIcon className="h-3 w-3" /> {status.label}
              </span>
            </div>
            <p className="mt-1 text-[13px] text-gray-500 dark:text-gray-400">
              Study Design · read-only
              {draft.submitted_at && (
                <> · {rs === 'in_review' ? 'Submitted' : 'Last submitted'} {fmtDateTime(draft.submitted_at)}</>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="-mr-2 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-white/[0.06] dark:hover:text-gray-200"
            aria-label="Close"
          >
            <X className="h-[18px] w-[18px]" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="mb-3 h-7 w-7 animate-spin text-primary-600 dark:text-primary-400" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading design details...</p>
            </div>
          ) : (
            <>
              {/* Review notes by state (FECOA-1603) */}
              {rs === 'in_review' && reviewCtx?.submission_note && (
                <div className="rounded-lg border border-gray-200/70 bg-gray-50/80 p-4 dark:border-white/[0.07] dark:bg-white/[0.03]">
                  <SectionLabel>Submitter&apos;s Note</SectionLabel>
                  <div className="flex gap-2">
                    <MessageSquare className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                    <p className="text-[13.5px] italic text-gray-700 dark:text-gray-200">&ldquo;{reviewCtx.submission_note}&rdquo;</p>
                  </div>
                </div>
              )}
              {rs === 'approved' && reviewCtx?.approval && (
                <div className="rounded-lg border border-gray-200/70 bg-gray-50/80 p-4 text-[13.5px] dark:border-white/[0.07] dark:bg-white/[0.03]">
                  <div className="flex flex-wrap items-center gap-1.5 font-medium text-gray-900 dark:text-gray-50">
                    <ShieldCheck className="h-4 w-4 flex-shrink-0 text-success-600 dark:text-success-400" />
                    Approved by {reviewCtx.approval.decided_by_name}
                    {reviewCtx.approval.decided_at && (
                      <span className="font-normal text-gray-500 dark:text-gray-400">· {fmtDateTime(reviewCtx.approval.decided_at)}</span>
                    )}
                  </div>
                  {reviewCtx.approval.message && (
                    <p className="ml-6 mt-0.5 italic text-gray-600 dark:text-gray-300">&ldquo;{reviewCtx.approval.message}&rdquo;</p>
                  )}
                </div>
              )}
              {rs === 'denied' && reviewCtx?.denial_history?.length > 0 && (
                <div className="rounded-lg border border-danger-600/15 bg-danger-50/70 p-4 text-[13.5px] dark:border-danger-400/20 dark:bg-danger-500/10">
                  <div className="flex flex-wrap items-center gap-1.5 font-medium text-danger-700 dark:text-danger-400">
                    <Ban className="h-4 w-4 flex-shrink-0" />
                    Denied by {reviewCtx.denial_history[0].decided_by_name}
                    {reviewCtx.denial_history[0].decided_at && (
                      <span className="font-normal text-danger-700/70 dark:text-danger-400/80">· {fmtDateTime(reviewCtx.denial_history[0].decided_at)}</span>
                    )}
                  </div>
                  {reviewCtx.denial_history[0].reason && (
                    <p className="ml-6 mt-0.5 italic text-gray-700 dark:text-gray-200">&ldquo;{reviewCtx.denial_history[0].reason}&rdquo;</p>
                  )}
                </div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-gray-200/80 bg-gray-200/80 sm:grid-cols-4 dark:border-white/[0.07] dark:bg-white/[0.07]">
                <StatTile label="Study start" value={startDay !== undefined ? `Day ${startDay}` : '—'} />
                <StatTile label={arms.length === 1 ? 'Arm' : 'Arms'} value={arms.length} />
                <StatTile label={schedules.length === 1 ? 'Schedule' : 'Schedules'} value={schedules.length} />
                <StatTile label={surveys.length === 1 ? 'Survey' : 'Surveys'} value={surveys.length} />
              </div>

              {/* Version chain (FECOA-1831): superseded versions are hidden from
                  the drafts table, so this is where the full amendment history
                  of a design stays visible. */}
              {chain.length > 1 && (
                <section>
                  <div className="mb-2 flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">Version History</h3>
                    <span className="badge badge-gray tabular-nums">{chain.length}</span>
                  </div>
                  <div className="divide-y divide-gray-100 rounded-lg border border-gray-200/80 dark:divide-white/[0.06] dark:border-white/[0.07]">
                    {chain.map((v) => {
                      const vMeta = STATUS_META[v.review_status] || STATUS_META.draft;
                      const isCurrent = v.id === draft.id;
                      return (
                        <div
                          key={v.id}
                          className={`flex items-center gap-3 px-3 py-2 text-[13px] ${isCurrent ? 'bg-primary-50/60 dark:bg-primary-400/[0.06]' : ''}`}
                        >
                          <span className="code-chip w-9 flex-shrink-0 justify-center">v{v.version_number ?? 1}</span>
                          <span className={`badge badge-dot flex-shrink-0 ${vMeta.cls}`}>{vMeta.label}</span>
                          <span className="truncate text-xs text-gray-500 dark:text-gray-400">
                            {v.review_status === 'published' && v.published_at
                              ? `Published ${fmtDateTime(v.published_at)}`
                              : `Updated ${fmtDateTime(v.updated_at)}`}
                          </span>
                          {isCurrent && (
                            <span className="ml-auto flex-shrink-0 text-[11px] font-medium text-primary-700 dark:text-primary-300">viewing</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}

              <div className="grid gap-6 sm:grid-cols-2">
                {/* Readiness */}
                <section>
                  <div className="mb-2 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">Readiness</h3>
                    <span className="text-xs font-medium tabular-nums text-gray-500 dark:text-gray-400">{doneCount}/{readiness.length} complete</span>
                  </div>
                  <div className="mb-3 h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-white/[0.08]" aria-hidden="true">
                    <div className="h-full rounded-full bg-primary-500" style={{ width: `${(doneCount / readiness.length) * 100}%` }} />
                  </div>
                  <div className="space-y-2">
                    {readiness.map((r, i) => (
                      <div key={i} className="flex items-center gap-2 text-[13px]">
                        {r.done
                          ? <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-success-600 dark:text-success-400" />
                          : <Circle className="h-4 w-4 flex-shrink-0 text-gray-300 dark:text-gray-600" />}
                        <span className={r.done ? 'text-gray-700 dark:text-gray-200' : 'text-gray-500 dark:text-gray-400'}>{r.label}</span>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Arms */}
                <section>
                  <div className="mb-2 flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">Arms</h3>
                    <span className="badge badge-gray tabular-nums">{arms.length}</span>
                  </div>
                  {arms.length > 0
                    ? <div className="flex flex-wrap gap-2">{arms.map((a, i) => <Chip key={i}>{a}</Chip>)}</div>
                    : <Muted>No arms configured</Muted>}
                </section>
              </div>

              {/* Schedules */}
              <section>
                <div className="mb-3 flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">Schedules</h3>
                  <span className="badge badge-gray tabular-nums">{schedules.length}</span>
                  {schedules.length > 0 && (
                    <button
                      onClick={() => setPreviewOpen(true)}
                      className="btn btn-secondary btn-sm ml-auto"
                      title="Visualize this schedule against a sample enrollment date"
                    >
                      <CalendarDays className="h-4 w-4" />
                      <span>Preview calendar</span>
                    </button>
                  )}
                </div>
                {schedules.length > 0 ? (
                  <div className="space-y-4">
                    {schedules.map((s, i) => (
                      <ScheduleReviewCard key={i} schedule={s} surveyName={surveyName} reminderName={reminderName} />
                    ))}
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed border-gray-200 p-8 text-center dark:border-white/10">
                    <Muted>No schedules configured</Muted>
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </div>

    {/* Sibling of the backdrop (not a child) so clicks inside the visualizer
        don't bubble to the backdrop's onClick and close both modals. */}
    <SchedulePreviewModal
      open={previewOpen}
      onClose={() => setPreviewOpen(false)}
      schedules={schedules}
      studyStartDay={startDay}
    />
    </>
  );
};

export default StudyDraftDetailModal;
